import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, ShieldCheck, AlertCircle, RefreshCw } from "lucide-react";
import SmartDateInput from "./SmartDateInput";
import { API } from "@/contexts/AuthContext";

/**
 * MOT Outcome Capture Form (MOT-2)
 * --------------------------------------------------------------------------
 * Tester-owned structured outcome capture. Mirrors `MOTOutcomeSubmission`
 * exactly. Strict invariants enforced client-side AND server-side:
 *
 *   • FAILED requires at least one defect with severity dangerous|major.
 *   • PASSED rejects any dangerous|major defect AND requires `mot_expiry_date`.
 *   • Defect text is preserved verbatim — never rewritten.
 *   • Severity is tester-asserted. The classifier derives location /
 *     system_group / component server-side but never alters severity.
 *
 * Modes:
 *   • mode="create"  — first submission (server returns 409 on duplicate).
 *   • mode="revise"  — revision: `reason` is mandatory, prior body is
 *                       preserved server-side under `revisions[]`.
 *
 * No upsell or predictive language. No LLM. Audit-tagged via the
 * /mot-outcome endpoints. See:
 *   /app/backend/services/intake/mot_intelligence/outcome/*
 */
// iter197t — Severity colour map for the DEFECTS RAISED rows. Mirrors
// the VI / FOH pricing convention exactly so testers and FOH share one
// mental model: Black = Dangerous, Red = Major (failure), Amber =
// Minor / Advisory.
const SEVERITY_ROW_CLASSES = {
  dangerous: "border-slate-900 bg-slate-50 border-l-4 border-l-slate-900",
  major:     "border-red-300   bg-red-50   border-l-4 border-l-red-600",
  minor:     "border-amber-300 bg-amber-50 border-l-4 border-l-amber-500",
  advisory:  "border-amber-300 bg-amber-50 border-l-4 border-l-amber-500",
  default:   "border-slate-200 bg-slate-50/40",
};
const SEVERITY_SELECT_CLASSES = {
  dangerous: "border-slate-900 bg-slate-900 text-white",
  major:     "border-red-400   bg-red-100   text-red-800 font-semibold",
  minor:     "border-amber-400 bg-amber-100 text-amber-900 font-semibold",
  advisory:  "border-amber-400 bg-amber-100 text-amber-900 font-semibold",
  default:   "border-slate-200 bg-white     text-slate-700",
};
// iter197u — Severity sort order for the DEFECTS RAISED list. Tester
// sees the most serious defects at the top regardless of capture order
// so they (and the FOH reviewer) can scan the failure-grade items first.
const SEVERITY_ORDER = {
  dangerous: 0,
  major:     1,
  minor:     2,
  advisory:  3,
  // Unknown / blank severities sink to the bottom.
  default:   9,
};

export default function MOTOutcomeCaptureForm({
  sessionId,
  // The job id is required for the "Pull from DVSA" action — DVSA history
  // is fetched per VRM, which lives on the job record. Optional for
  // backwards compatibility: when missing, the Pull button hides.
  jobId = null,
  initial = null,
  mode = "create",
  // iter194 — when set (along with mode==="create"), the form posts to
  // /mot-outcome/supersede-baseline so an existing historical baseline
  // on the session doesn't trigger 409 ("outcome_already_submitted").
  supersedeBaselineOutcomeId = null,
  onSubmitted,
  onCancel,
  // FRICTIONLESS MOT WORKFLOW.
  // When true (default for fresh-create mode), the form auto-pulls from
  // DVSA the moment it mounts — so the tester opens the page and finds
  // every field already populated, ready for a quick review-and-submit.
  // No clicks required. Silent fail if DVSA hasn't updated yet — the
  // form falls back to a blank manual form with the Pull button still
  // available for retry.
  autoPullOnMount = true,
  // Stage prefill (Stage 2 → Stage 3 continuity). When the tester opens the
  // MOT workspace from a VI that has already captured mileage, the form
  // seeds the odometer field from that value so they don't re-type it.
  // The tester can override — useful if the vehicle was road-tested
  // between the VI being completed and the MOT bay running.
  prefillOdometer = null,
  prefillOdometerUnit = null,
}) {
  const [severities, setSeverities] = useState([]);
  const [loadingSev, setLoadingSev] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // DVSA pull state — populated by `Pull from DVSA` button. Stays in-form
  // until the tester submits, so they have time to review and override.
  const [pulling, setPulling] = useState(false);
  const [dvsaWarnings, setDvsaWarnings] = useState([]);
  const [pulledAt, setPulledAt] = useState(null);
  const [pulledSource, setPulledSource] = useState(null);

  // Capture body — initialised from `initial` for revisions, else from
  // the VI-prefill on a fresh "create" form.
  const isCreateFresh = mode === "create" && !initial;
  const [testResult, setTestResult] = useState(initial?.test_result || "PASSED");
  const [testCompletedAt, setTestCompletedAt] = useState(
    initial?.test_completed_at?.slice(0, 16) || nowLocalDateTime()
  );
  const initialOdometer =
    initial?.odometer_reading ??
    (isCreateFresh && prefillOdometer != null && prefillOdometer !== ""
      ? String(prefillOdometer)
      : "");
  const [odometer, setOdometer] = useState(initialOdometer);
  const [odometerUnit, setOdometerUnit] = useState(
    initial?.odometer_unit ||
      (isCreateFresh && prefillOdometerUnit ? prefillOdometerUnit : "mi")
  );
  // Track whether the current odometer value still matches the VI prefill.
  // Surfaces a calm "prefilled from VI" hint until the tester edits the
  // field — at which point the hint hides (they've taken ownership).
  const odometerIsPrefilled =
    isCreateFresh &&
    prefillOdometer != null &&
    prefillOdometer !== "" &&
    String(odometer) === String(prefillOdometer);
  const [testNumber, setTestNumber] = useState(initial?.test_number || "");
  const [motExpiryDate, setMotExpiryDate] = useState(initial?.mot_expiry_date || "");
  const [testerNotes, setTesterNotes] = useState(initial?.tester_notes || "");
  const [defects, setDefects] = useState(
    (initial?.defects || []).map((d) => ({
      text: d.text || "",
      severity: d.severity || "advisory",
      location_hint: d.location_hint || d.location || "",
      component_hint: d.component_hint || d.component || "",
      rfr_code: d.rfr_code || "",
    }))
  );
  const [revisionReason, setRevisionReason] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/intake/mot/outcome/severities`)
      .then((r) => setSeverities(r.data?.severities || []))
      .catch(() => setSeverities([]))
      .finally(() => setLoadingSev(false));
  }, []);

  // ---- Auto-pull from DVSA on mount --------------------------------------
  // Fires once, only on a fresh create form, and only when a jobId is
  // available. The tester opens the page and the form is already filled
  // in — minimising clicks to the absolute minimum (read → submit).
  // Silent on failure: if DVSA hasn't updated yet, the form simply shows
  // empty and the manual Pull button remains usable.
  useEffect(() => {
    if (!autoPullOnMount) return;
    if (!jobId) return;
    if (mode !== "create") return;
    if (initial) return;
    // Defer one tick so React has the initial state mounted before we
    // start overwriting setters from the async handler.
    const t = setTimeout(() => { pullFromDvsa({ silentOnError: true }); }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPullOnMount, jobId, mode]);

  // ---- Defect row helpers ------------------------------------------------

  const addDefect = () =>
    setDefects((prev) => [
      ...prev,
      { text: "", severity: "advisory", location_hint: "", component_hint: "", rfr_code: "" },
    ]);
  const removeDefect = (idx) =>
    setDefects((prev) => prev.filter((_, i) => i !== idx));
  const updateDefect = (idx, field, value) =>
    setDefects((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d))
    );

  // ---- Pull from DVSA ----------------------------------------------------
  // Fetches the freshly-completed MOT test from the DVSA history API and
  // pre-fills every field on the form. The tester then reviews & submits.
  //
  // What gets overwritten:
  //   - testResult, testCompletedAt, motExpiryDate, odometer, odometerUnit,
  //     testNumber, defects[].
  // What does NOT get overwritten:
  //   - testerNotes (the tester's own free text).
  //   - revisionReason (revision-mode field).
  //
  // The pull is a READ-ONLY preview server-side — no `mot_outcomes`
  // document is created until the tester explicitly submits.
  const pullFromDvsa = async (opts = {}) => {
    const silentOnError = !!opts.silentOnError;
    if (!jobId) {
      if (!silentOnError) setError("Job context missing — cannot pull from DVSA.");
      return;
    }
    setError(null);
    setPulling(true);
    try {
      const r = await axios.post(
        `${API}/jobs/${jobId}/mot-outcome/refresh-from-dvsa`
      );
      const d = r.data || {};
      setTestResult(d.test_result || "PASSED");
      if (d.test_completed_at) {
        setTestCompletedAt(d.test_completed_at.slice(0, 16));
      }
      if (d.mot_expiry_date) setMotExpiryDate(d.mot_expiry_date);
      if (d.odometer_reading != null) setOdometer(String(d.odometer_reading));
      if (d.odometer_unit) setOdometerUnit(d.odometer_unit);
      if (d.test_number) setTestNumber(d.test_number);
      const mappedDefects = (d.defects || []).map((x) => ({
        text:           x.text || "",
        severity:       x.severity || "advisory",
        location_hint:  x.location_hint || "",
        component_hint: x.component_hint || "",
        rfr_code:       x.rfr_code || "",
      }));
      setDefects(mappedDefects);
      setDvsaWarnings(d.warnings || []);
      setPulledAt(new Date().toISOString());
      setPulledSource(d.source || null);
    } catch (err) {
      if (silentOnError) {
        // Auto-pull only: stay quiet, the tester can press the manual
        // button if they want to retry. Common when DVSA hasn't updated
        // the history feed yet.
        return;
      }
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Could not reach DVSA — try again in a moment."
      );
    } finally {
      setPulling(false);
    }
  };

  // ---- Submit ------------------------------------------------------------

  const buildPayload = () => {
    const cleaned = defects
      .filter((d) => (d.text || "").trim())
      .map((d) => ({
        text: d.text.trim(),
        severity: d.severity,
        ...(d.location_hint ? { location_hint: d.location_hint.trim() } : {}),
        ...(d.component_hint ? { component_hint: d.component_hint.trim() } : {}),
        ...(d.rfr_code ? { rfr_code: d.rfr_code.trim() } : {}),
      }));
    return {
      test_result: testResult,
      test_completed_at: new Date(testCompletedAt).toISOString(),
      odometer_reading: Number(odometer),
      odometer_unit: odometerUnit,
      test_number: testNumber.trim() || null,
      mot_expiry_date: motExpiryDate || null,
      tester_notes: testerNotes.trim() || null,
      defects: cleaned,
    };
  };

  const submit = async () => {
    setError(null);
    // Client-side guards mirror server invariants — fail fast.
    if (!odometer || Number(odometer) < 0) {
      setError("Odometer reading is required and must be positive.");
      return;
    }
    if (testResult === "PASSED" && !motExpiryDate) {
      setError("PASSED outcome requires an MOT expiry date.");
      return;
    }
    const terminal = defects.some(
      (d) => d.text.trim() && (d.severity === "dangerous" || d.severity === "major")
    );
    if (testResult === "FAILED" && !terminal) {
      setError(
        "FAILED outcome requires at least one defect with severity Dangerous or Major (failure)."
      );
      return;
    }
    if (testResult === "PASSED" && terminal) {
      setError(
        "PASSED outcome cannot include Dangerous or Major defects. Adjust the severity or the result."
      );
      return;
    }
    if (mode === "revise" && revisionReason.trim().length < 4) {
      setError("Revision reason is required (at least 4 characters).");
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      let res;
      if (mode === "revise") {
        res = await axios.post(
          `${API}/intake/sessions/${sessionId}/mot-outcome/revise`,
          { reason: revisionReason.trim(), revised: payload }
        );
      } else if (supersedeBaselineOutcomeId) {
        // iter194 — historical baseline supersede path.
        res = await axios.post(
          `${API}/intake/sessions/${sessionId}/mot-outcome/supersede-baseline`,
          payload
        );
      } else {
        res = await axios.post(
          `${API}/intake/sessions/${sessionId}/mot-outcome`,
          payload
        );
      }
      onSubmitted?.(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "outcome_already_submitted") {
        setError(
          "An outcome already exists for this session. Open the existing outcome to record a revision."
        );
      } else if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        // Pydantic validation error — surface the first message verbatim.
        setError(detail[0]?.msg || "Validation error.");
      } else {
        setError("Could not submit the MOT outcome.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white"
      data-testid="mot-outcome-capture-form"
      data-mode={mode}
    >
      <header className="px-6 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-teal-600" />
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              {mode === "revise" ? "Revise MOT outcome" : "Record MOT outcome"}
            </p>
          </div>
          {jobId && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={pullFromDvsa}
              disabled={pulling}
              className="text-[13px] border-teal-300 text-teal-800 hover:bg-teal-50 flex-shrink-0"
              data-testid="mot-outcome-pull-dvsa-btn"
              title="Fetch the freshly-completed MOT test from DVSA history and pre-fill this form"
            >
              {pulling ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Pulling from DVSA…
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  {pulledAt ? "Re-pull from DVSA" : "Pull from DVSA"}
                </>
              )}
            </Button>
          )}
        </div>
        <p className="text-[15px] text-slate-700 mt-1 leading-snug">
          {mode === "revise"
            ? "Append a correction. The original submission stays on record."
            : "Tester-owned. Severity is your call. Wording is preserved verbatim."}
        </p>
        {pulledAt && (
          <div
            className="mt-3 rounded-md border border-teal-200 bg-teal-50/60 px-3 py-2"
            data-testid="mot-outcome-pulled-banner"
            data-source={pulledSource || ""}
          >
            <p className="text-[12.5px] text-teal-900 leading-snug">
              <span className="font-semibold">Auto-filled from DVSA</span>{" "}
              {pulledSource === "mock" && (
                <span className="text-amber-800">(mock data — dev mode)</span>
              )}{" "}
              · Result, expiry, mileage and any advisories are
              already in. Review and submit — or edit anything that needs
              your tester judgement.
            </p>
            {dvsaWarnings.length > 0 && (
              <ul className="mt-1.5 list-disc list-inside text-[12px] text-amber-800 space-y-0.5">
                {dvsaWarnings.map((w, i) => (
                  <li key={i} data-testid={`mot-outcome-dvsa-warning-${i}`}>
                    {w}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </header>

      <div className="px-6 py-5 space-y-6">
        {/* Result */}
        <div data-testid="mot-outcome-result-group">
          <label className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-semibold block mb-2">
            Result
          </label>
          <div className="flex gap-3">
            {["PASSED", "FAILED"].map((r) => {
              const active = testResult === r;
              const isFail = r === "FAILED";
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setTestResult(r)}
                  className={`flex-1 px-4 py-2.5 rounded-md border text-[15px] font-semibold transition-colors ${
                    active
                      ? isFail
                        ? "bg-red-700 text-white border-red-700"
                        : "bg-emerald-700 text-white border-emerald-700"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`mot-outcome-result-${r.toLowerCase()}`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>

        {/* Test metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Test completed at" testid="field-completed-at">
            <Input
              type="datetime-local"
              value={testCompletedAt}
              onChange={(e) => setTestCompletedAt(e.target.value)}
              className="text-[15px]"
              data-testid="mot-outcome-completed-at"
            />
          </Field>
          <Field label={`Odometer (${odometerUnit})`} testid="field-odometer">
            <div className="flex gap-2">
              <Input
                type="number"
                min="0"
                inputMode="numeric"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                placeholder="e.g. 78910"
                className="text-[15px] flex-1"
                data-testid="mot-outcome-odometer"
              />
              <select
                value={odometerUnit}
                onChange={(e) => setOdometerUnit(e.target.value)}
                className="px-3 rounded-md border border-slate-200 text-[14px] text-slate-700 bg-white"
                data-testid="mot-outcome-odometer-unit"
              >
                <option value="mi">mi</option>
                <option value="km">km</option>
              </select>
            </div>
            {odometerIsPrefilled && (
              <p
                className="mt-1.5 text-[12px] text-teal-700/85 italic leading-snug"
                data-testid="mot-outcome-odometer-prefilled-hint"
              >
                Pre-filled from the VI mileage. Adjust if the vehicle was road-tested since.
              </p>
            )}
          </Field>
          <Field
            label={`MOT expiry${testResult === "PASSED" ? " · required" : ""}`}
            testid="field-expiry"
          >
            <SmartDateInput
              value={motExpiryDate || ""}
              onChange={(e) => setMotExpiryDate(e.target.value)}
              dataTestid="mot-outcome-expiry"
              hint
            />
          </Field>
          <Field label="DVSA test number (optional)" testid="field-test-number">
            <Input
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="12-digit reference"
              className="text-[15px]"
              data-testid="mot-outcome-test-number"
            />
          </Field>
        </div>

        {/* Defects */}
        <div data-testid="mot-outcome-defects-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
              Defects raised
            </p>
            <p className="text-[12.5px] text-slate-400 italic">
              {testResult === "FAILED"
                ? "At least one Dangerous or Major required"
                : "Advisory / minor / tester-notes only"}
            </p>
          </div>
          <ul className="space-y-3">
            {defects.length === 0 && (
              <li
                className="text-[14px] text-slate-500 italic px-3.5 py-3 border border-dashed border-slate-200 rounded-md"
                data-testid="mot-outcome-defects-empty"
              >
                No defects recorded yet.
              </li>
            )}
            {/* iter197u — Render defects in severity order (Dangerous →
                Major → Minor → Advisory) so the most serious items
                surface to the top of the tester's screen. We keep the
                ORIGINAL array index alongside each entry so
                updateDefect(i) / removeDefect(i) still target the
                right row even after re-ordering. */}
            {defects
              .map((d, origIdx) => ({ d, origIdx }))
              .sort((a, b) => {
                const ka = SEVERITY_ORDER[(a.d.severity || "").toLowerCase()] ?? SEVERITY_ORDER.default;
                const kb = SEVERITY_ORDER[(b.d.severity || "").toLowerCase()] ?? SEVERITY_ORDER.default;
                if (ka !== kb) return ka - kb;
                // Stable within same severity — preserve capture order.
                return a.origIdx - b.origIdx;
              })
              .map(({ d, origIdx: i }) => {
              const sevKey = (d.severity || "advisory").toLowerCase();
              const rowCls = SEVERITY_ROW_CLASSES[sevKey] || SEVERITY_ROW_CLASSES.default;
              const selCls = SEVERITY_SELECT_CLASSES[sevKey] || SEVERITY_SELECT_CLASSES.default;
              return (
              <li
                key={i}
                className={`rounded-md border px-4 py-3.5 space-y-2.5 ${rowCls}`}
                data-testid={`mot-outcome-defect-row-${i}`}
                data-severity={sevKey}
              >
                <div className="flex items-start gap-2">
                  <Textarea
                    value={d.text}
                    onChange={(e) => updateDefect(i, "text", e.target.value)}
                    placeholder="Defect wording — exact tester observation"
                    rows={2}
                    className="text-[14.5px] resize-none flex-1"
                    data-testid={`mot-outcome-defect-text-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeDefect(i)}
                    className="text-slate-400 hover:text-red-700 p-1.5 transition-colors flex-shrink-0"
                    aria-label="Remove defect"
                    data-testid={`mot-outcome-defect-remove-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <select
                    value={d.severity}
                    onChange={(e) => updateDefect(i, "severity", e.target.value)}
                    className={`px-3 py-2 rounded-md border text-[14px] transition-colors ${selCls}`}
                    data-testid={`mot-outcome-defect-severity-${i}`}
                  >
                    {loadingSev ? (
                      <option>Loading…</option>
                    ) : (
                      severities.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))
                    )}
                  </select>
                  <Input
                    value={d.location_hint}
                    onChange={(e) => updateDefect(i, "location_hint", e.target.value)}
                    placeholder="Location (optional, e.g. NSF)"
                    className="text-[14px]"
                    data-testid={`mot-outcome-defect-location-${i}`}
                  />
                  <Input
                    value={d.component_hint}
                    onChange={(e) => updateDefect(i, "component_hint", e.target.value)}
                    placeholder="Component (optional)"
                    className="text-[14px]"
                    data-testid={`mot-outcome-defect-component-${i}`}
                  />
                </div>
              </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={addDefect}
            className="mt-3 inline-flex items-center gap-2 text-[14px] font-semibold text-teal-700 hover:text-teal-900"
            data-testid="mot-outcome-add-defect-btn"
          >
            <Plus className="w-4 h-4" /> Add defect
          </button>
        </div>

        {/* Tester notes */}
        <Field label="Tester notes (optional)" testid="field-tester-notes">
          <Textarea
            value={testerNotes}
            onChange={(e) => setTesterNotes(e.target.value)}
            placeholder="Anything else relevant for the FOH handover"
            className="text-[14.5px] min-h-[72px] resize-none"
            data-testid="mot-outcome-tester-notes"
          />
        </Field>

        {/* Revision reason */}
        {mode === "revise" && (
          <Field
            label="Reason for revision · required"
            testid="field-revision-reason"
          >
            <Input
              value={revisionReason}
              onChange={(e) => setRevisionReason(e.target.value)}
              placeholder="e.g. Disc pitting noted after pad inspection"
              className="text-[14.5px]"
              data-testid="mot-outcome-revision-reason"
            />
          </Field>
        )}

        {/* Error */}
        {error && (
          <div
            className="rounded-md border border-red-200 bg-red-50 px-3.5 py-2.5 flex items-start gap-2"
            data-testid="mot-outcome-error"
          >
            <AlertCircle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
            <p className="text-[13.5px] text-red-900 leading-snug">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
              className="text-[14px]"
              data-testid="mot-outcome-cancel-btn"
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-semibold h-10 px-5"
            data-testid="mot-outcome-submit-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {mode === "revise" ? "Submit revision" : "Submit outcome"}
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-semibold block mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function nowLocalDateTime() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
